import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import RightBar from "../../../../layouts/Dashboard/components/rightbar";
import AllOrderDetailsRightBar from "../../../../pages/all-orders/components/rightsidebar/rightsidebar_details";
import { OrderDetailsData } from "../../../../contexts";
import GetAllOrders from "../../../../hooks/fetch-hooks/fetch-all/get-all-orders";
import { PHARMACY_TABS, DEFAULT_PHARMACY_TAB } from "./subTabs";
import PharmacyOrdersTableInstance from "./table-instance";
import ColumnsModal from "./ColumnsModal";
import { loadHiddenColumns, saveHiddenColumns } from "./columnPrefs";
import { SORT_PRESETS, loadSortPreset, saveSortPreset } from "./sortPresets";
import { DEFAULT_PAGE_SIZE } from "../../../constants/pagination";
import OrderStatusFilter from "../../../components/OrderStatusFilter";
import {
    ORDER_STATUS_FILTERS,
    parseStatusFilterParam,
    statusesForFilters,
} from "../../../constants/order-status-filters";

// Pharmacist Orders -- /v2/pharmacy/orders.
//
// Previously a v2-native page built on a bespoke ListPage shell (read-only: no way to
// change a status, mark packed, or print an address). Rebuilt on the same legacy
// orderTable stack the ops console's own Orders screen uses
// (../../ops/pages/orders-legacy) -- see PharmacyOrdersTableInstance (./table-instance)
// for the column assembly and Cell/Cell2/CellChannel/CellSource reuse, and
// COLUMNS_FUCTIONALITIES.PHARMACY_ORDERS (src/components/orderTable/Constants.js) for
// which of legacy's toolbar/action affordances this console gets (Edit, Status,
// Download, Print Multiple Address, select-all -- no Reorder, no Delete).
//
// Four things carried forward from the v2-native build, all still real requirements:
//   1. The four fulfilment tabs (./subTabs.js) -- a status SET per tab, not one status
//      each, so a row in an unlisted-but-real status never silently vanishes.
//   2. The priority sort -- now one of four presets the pharmacist can switch between
//      (./sortPresets.js), remembered via localStorage. Each preset is a set of
//      orderBy/order params sent to the backend, which sorts BEFORE paginating --
//      see sortPresets.js for why that matters (it didn't used to).
//   3. Days Left, computed via daysLeftUntil and never clamped at zero (./columns.jsx).
//   4. The NCR badge, folded into the Order City column (./columns.jsx).
//   5. Status chips (src/v2/components/OrderStatusFilter), shared with every console's
//      Orders tab. They NARROW the active sub-tab rather than replace it: the request
//      sends the overlap of the tab's set and the chips' sets, and only chips that
//      overlap the tab are offered. An empty overlap fetches nothing -- sending an
//      empty `status` would mean "every status" to the API.

function PharmacyOrdersPage() {
    const [searchParams, setSearchParams] = useSearchParams();

    const [data, setData] = useState();
    const [rightBarOpen, setRightBarOpen] = useState({ name: "pharmacy-orders", open: false });
    const changeRightBarOpen = () => {
        if (!rightBarOpen.open) setRightBarOpen({ ...rightBarOpen, open: true });
    };
    const changeRightBarClose = () => setRightBarOpen({ ...rightBarOpen, open: false });

    const [tab, setTab] = useState(searchParams.get("tab") || DEFAULT_PHARMACY_TAB);
    const activeTab = PHARMACY_TABS.find((entry) => entry.id === tab) || PHARMACY_TABS[0];
    const [statusFilters, setStatusFilters] = useState(() => parseStatusFilterParam(searchParams.get("status")));

    // "All Orders" has no status set, so every chip applies there.
    const statusOptions = activeTab.statuses.length
        ? ORDER_STATUS_FILTERS.filter((filter) => filter.statuses.some((status) => activeTab.statuses.includes(status)))
        : ORDER_STATUS_FILTERS;
    // Only chips this tab offers count -- a ?status= from a link can name one it hides.
    const activeStatusFilters = statusFilters.filter((id) => statusOptions.some((option) => option.id === id));

    // null = the chips and the tab share no status, so nothing can match.
    const requestStatuses = () => {
        const chipStatuses = statusesForFilters(activeStatusFilters);
        if (!chipStatuses.length) return activeTab.statuses;
        if (!activeTab.statuses.length) return chipStatuses;
        const overlap = activeTab.statuses.filter((status) => chipStatuses.includes(status));
        return overlap.length ? overlap : null;
    };

    const [tableData, setTableData] = useState([]);
    const [total, setTotal] = useState(0);
    const [pageSort, setPageSort] = useState([]);
    const [pageindex, setPageIndex] = useState(Number(searchParams.get("page")) || 1);
    const [value, setValue] = useState(searchParams.get("search") || "");
    const [debouncedSearch, setDebouncedSearch] = useState(value);
    const [columnsOpen, setColumnsOpen] = useState(false);
    const [hiddenColumns, setHiddenColumns] = useState(() => loadHiddenColumns());
    const [activePreset, setActivePreset] = useState(() => loadSortPreset());

    const getData = async () => {
        const statuses = requestStatuses();
        if (statuses === null) {
            setTableData([]);
            setTotal(0);
            return;
        }
        const preset = SORT_PRESETS[activePreset];
        const fetchedData = await GetAllOrders(
            {
                page: pageindex,
                size: DEFAULT_PAGE_SIZE,
                patientName: debouncedSearch,
                userName: debouncedSearch,
                type: "ALL",
                status: statuses.join(","),
                orderBy: preset.orderBy,
                order: preset.order,
            },
            ""
        );
        const rows = fetchedData?.data?.data?.rows || [];
        setTableData(rows);
        setTotal(fetchedData?.data?.data?.count || 0);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(value);
            setPageIndex(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [value]);

    useEffect(() => {
        getData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageindex, debouncedSearch, tab, activePreset, statusFilters]);

    useEffect(() => {
        setSearchParams({ page: pageindex, search: value, tab, ...(statusFilters.length ? { status: statusFilters.join(",") } : {}) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageindex, value, tab, statusFilters]);

    const maxValues = Math.ceil(total / DEFAULT_PAGE_SIZE) || 1;

    // Dead in practice: PHARMACY_ORDERS's COLUMNS_FUCTIONALITIES has no "Delete", so
    // this is wired into the table's Action cell but never reachable -- same as the
    // no-op ReOrder plumbing in table-instance.jsx.
    const handleDeleteRow = (id) => setTableData((prev) => prev.filter((item) => item.id !== id));

    return (
        <div className="p-7 h-full overflow-y-scroll bg-gray-50">
            <RightBar rightBarOpenAction={changeRightBarOpen} rightBarCloseAction={changeRightBarClose} value={rightBarOpen.open} data={data}>
                <AllOrderDetailsRightBar {...data} />
            </RightBar>
            <OrderDetailsData.Provider value={{ data, setData, OrderType: "PHARMACY_ORDERS", setOrderType: () => {}, isReorder: false }}>
                <div className="flex gap-4 items-center relative">
                    <input
                        value={value}
                        className="px-6 font-sans w-[80%] bg-slate-100 py-4"
                        onChange={(e) => setValue(e.target.value)}
                        placeholder={`${total} records...`}
                        style={{ fontSize: "1.1rem", border: "0" }}
                    />
                </div>
                <div className="flex justify-start items-center my-[1rem] gap-2 flex-wrap">
                    {PHARMACY_TABS.map((entry) => (
                        <button
                            key={entry.id}
                            onClick={() => {
                                setTab(entry.id);
                                // Chips are offered per tab, so a selection may not exist
                                // on the next one -- start the new tab unfiltered.
                                setStatusFilters([]);
                                setPageIndex(1);
                            }}
                            className={`${
                                entry.id === tab ? "bg-elm-500 text-white-50" : "bg-transparent text-elm-500"
                            } border-[1px] border-elm-500 px-4 hover:bg-elm-500 hover:text-white-50 font-semibold py-2 rounded-[9px]`}
                        >
                            {entry.label}
                        </button>
                    ))}
                    <label className="flex items-center gap-2 text-sm font-semibold text-elm-500 ml-2">
                        Sort:
                        <select
                            value={activePreset}
                            onChange={(e) => {
                                // A different sort means a genuinely different page 1
                                // (it's applied server-side, before pagination), not
                                // just a re-ordering of what's already on screen --
                                // reset to page 1 the same way changing tabs does.
                                setActivePreset(e.target.value);
                                saveSortPreset(e.target.value);
                                setPageIndex(1);
                            }}
                            className="border-[1px] border-elm-500 rounded-[9px] px-2 py-1.5 font-sans text-sm text-elm-500"
                        >
                            {Object.entries(SORT_PRESETS).map(([id, preset]) => (
                                <option key={id} value={id}>
                                    {preset.label}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
                <div className="mb-3">
                    <OrderStatusFilter
                        selected={activeStatusFilters}
                        options={statusOptions}
                        onChange={(ids) => {
                            setStatusFilters(ids);
                            setPageIndex(1);
                        }}
                    />
                </div>
                <button
                    className="border-[1px] border-elm-500 text-elm-500 px-4 py-1 rounded-md mb-2"
                    onClick={() => setColumnsOpen(true)}
                >
                    Columns
                </button>
                {columnsOpen && (
                    <ColumnsModal
                        hiddenColumns={hiddenColumns}
                        onApply={(next) => {
                            setHiddenColumns(next);
                            saveHiddenColumns(next);
                            setColumnsOpen(false);
                        }}
                        onClose={() => setColumnsOpen(false)}
                    />
                )}
                <p className="text-xs text-gray-500 mb-2">{SORT_PRESETS[activePreset].caption}</p>
                <PharmacyOrdersTableInstance
                    tableData={tableData}
                    changeRightBarOpen={changeRightBarOpen}
                    pageCount={total}
                    setPageSort={setPageSort}
                    pageSort={pageSort}
                    handleDeleteRow={handleDeleteRow}
                    refetchOrders={getData}
                    hiddenColumns={hiddenColumns}
                />
                <div className="btn_div">
                    <button onClick={() => setPageIndex((count) => Number(count) - 1)} disabled={pageindex <= 1} className="previous">
                        Previous
                    </button>
                    <span className="pageindex">{`Page ${pageindex} of ${maxValues}`}</span>
                    <button onClick={() => setPageIndex((count) => Number(count) + 1)} disabled={pageindex >= maxValues} className="next">
                        Next
                    </button>
                </div>
            </OrderDetailsData.Provider>
        </div>
    );
}

export default PharmacyOrdersPage;
